import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { combineLatest, Subject, takeUntil, withLatestFrom } from "rxjs";
import { Store } from '@ngrx/store';
import {
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { percentFormatter } from '../../../shared/utilities/PercentFormatter';
import { ItToAccountDetails, TaxWithholdingFields, TransactionForm } from '../../../create-request/models/form/TransactionForm';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { selectInternalTransferInfo, selectInternalTransfersInfo, selectInternalTransfersToAccountProperties, selectIsFromEdit, selectIsFromEditSubmittedTransaction, selectIsOneTimeTotalDist, selectTransactionForm, selectTwe } from '../../../create-request/reducers';
import {
    amountTypeDict,
    Constants,
    distributionReasons,
    fedTaxErrorMap,
    RothConversion,
    RothWarnings,
    stateCodes,
    stateTaxErrorMap,
    transactionDetailLabels,
} from '../../../shared/utilities/Constants';
import { percentAllowedChars } from '../../../shared/utilities/regex-patterns';
import { selectClassifications, selectNraIndicator, selectStateCode } from "../../../account-info-header/reducers";
import { selectTaxWithholding } from "../../../transaction-view/reducers";
import { InternalTransfersInfo } from "../../models/internal-transfer/InternalTransfersInfo";
import { DistributionReason } from "../../models/internal-transfer/DistributionReason";
import { Classifications } from "../../../account-info-header/models";
import { TransactionFormActions } from "../../actions";
import { ofType, Actions } from "@ngrx/effects";
import { FeatureFlagService } from "../../../shared/services/feature-flag-service";
import { selectIsEnableSpecialStateTax } from "../../../feature-flags/reducers";

@Component({
  standalone: false,
    selector: 'mm-it-multiple-tax-withholdings',
    templateUrl: './it-multiple-tax-withholdings.component.html',
    styleUrls: ['./it-multiple-tax-withholdings.component.scss'],
})
export class ItMultipleTaxWithholdingsComponent implements OnInit, OnDestroy {
    @Input() parentForm: UntypedFormGroup;
    @Input() editFormDetails: TransactionForm | undefined;
    taxWithholdings: TaxWithholdingFields;
    destroy$: Subject<boolean> = new Subject<boolean>();
    nraIndicator: boolean;
    nraText: string = Constants.nraText;
    percentAllowedChars = percentAllowedChars;
    stateTaxLabel: string = transactionDetailLabels.stateTax;
    isEarlyDistributionReason: boolean = false;
    isFromEditSubmittedTransaction: boolean;
    stateCode: string;
    itsInfo: InternalTransfersInfo;
    selectedDistributionReasonList: {toAccount: string, accountRegistration: string, accountRegTypeCode: string, distributionReason: DistributionReason}[] 
    transactionFormState: TransactionForm;
    accountClassifications: Classifications;
    zeroPercentTitleWarningText = RothWarnings.ZeroPercentWarningBannerTitle;
    zeroPercentWarningText: string;
    convWarningText: string;
    isOneTimeTotalDist: boolean;
    accountsSelectedWithZeroDist: { toAccount: string; accountRegistration: string; accountRegTypeCode: string; distributionReason: DistributionReason; }[];
    isEdit: boolean = false;
    internationalFedTaxError: string = fedTaxErrorMap.internationalFedTaxError;
    hasInternationalFedTaxError: boolean = false;
    hasOnlyStateTaxError: boolean = false;
    specialStateTaxFFEnabled: boolean = false;

    constructor(
        public validationService: ValidateInputService,
        private fb: UntypedFormBuilder,
        private store: Store,
        public actions$: Actions,
        private featureFlagService: FeatureFlagService 
    ) { }

    ngOnInit(): void {
        this.store.select(selectIsEnableSpecialStateTax).pipe(takeUntil(this.destroy$)).subscribe(enabled => this.specialStateTaxFFEnabled = enabled);
        this.store.select(selectInternalTransfersToAccountProperties)
            .pipe(
            takeUntil(this.destroy$),
            withLatestFrom(this.store.select(selectIsFromEdit), this.store.select(selectInternalTransferInfo))
            )
            .subscribe(([propertiesById, isEdit, itInfo]) => {
                this.isEdit = isEdit;
                if (
                  isEdit ||
                  (!this.taxWithholdings &&
                    Object.values(propertiesById).length)
                ) {
                  this.taxWithholdings = {
                    stateTaxPercent: isEdit ? this.editFormDetails?.transactionDetails?.taxWithholdingsFormGroup?.stateTaxPercent ?? ''
                        : Object.values(propertiesById)[0]?.inputIraFields.stateTaxPercent ?? '',
                    fedTaxPercent: isEdit ? this.editFormDetails?.transactionDetails?.taxWithholdingsFormGroup?.fedTaxPercent ?? ''
                        : Object.values(propertiesById)[0]?.inputIraFields.fedTaxPercent ?? '',
                    fedTaxAmount: '',
                    stateTaxAmount: '',
                    distributionReason: '',
                    distributionReasonMap: [],
                    defaultFedTax: itInfo?.iraFields?.fedTaxPercent ?? "0%",
                    defaultStateTax: itInfo?.iraFields?.stateTaxPercent ?? "0%"
                  };
                }
              });

        this.store.select(selectIsFromEditSubmittedTransaction)
        .pipe(takeUntil(this.destroy$))
        .subscribe((val) => this.isFromEditSubmittedTransaction = val);

        combineLatest([
            this.store.select(selectStateCode),
            this.store.select(selectTaxWithholding)
            ]).pipe(takeUntil(this.destroy$))
            .subscribe(([stateCode, taxWithholding]) => {
                if (this.isFromEditSubmittedTransaction) {
                this.stateCode = taxWithholding.stateCode ?? '';
                } else {
                this.stateCode = stateCode;
                }
            });

        const taxWithholdingsFormGroup = this.fb.group({
            fedTaxPercent: this.GetFedTaxFormControl(),
            stateTaxPercent: this.GetStateTaxFormControl(),
        });
        this.parentForm.setControl(
            'taxWithholdingsFormGroup',
            taxWithholdingsFormGroup
        );
        this.taxWithholdingsFormGroup.setValidators(
            CustomValidators.sumDoesNotExceed99Point99(
                'fedTaxPercent',
                'stateTaxPercent'
            )
        );
    
        this.store.select(selectNraIndicator).pipe(takeUntil(this.destroy$)).subscribe(nraIndicator => {
            this.nraIndicator = nraIndicator ?? false;
          });
          
        this.store.select(selectStateCode).pipe(takeUntil(this.destroy$)).subscribe((stateCode) => {
            if (stateCode && !this.nraIndicator) {
                this.stateTaxLabel = `${transactionDetailLabels.stateTax} (${stateCode})`;
            }
        });

        this.store.select(selectIsOneTimeTotalDist).pipe(takeUntil(this.destroy$)).subscribe((isOneTimeTotalDist) => {
            this.isOneTimeTotalDist = isOneTimeTotalDist
        });

        combineLatest([
            this.store.select(selectTransactionForm),
            this.store.select(selectInternalTransfersInfo),
        ]).pipe(takeUntil(this.destroy$)).subscribe(([transactionFormState, itInfo]) => {
            if ("toAccounts" in transactionFormState.transactionDetails) { 
                this.selectedDistributionReasonList = transactionFormState?.transactionDetails?.toAccounts?.map((toAccountInput: ItToAccountDetails) => {
                    return  {
                        toAccount : toAccountInput.toAccount,
                        distributionReason: itInfo.iraFields[toAccountInput.toAccount]?.distributionReasonMap.find(distributionMap => {
                            return distributionMap.description === toAccountInput.iraDistributionType
                        }) ?? new DistributionReason(),
                        accountRegistration: itInfo.toAccountInfo[toAccountInput.toAccount]?.accountRegistration[0] ?? "",
                        accountRegTypeCode: itInfo.toAccountInfo[toAccountInput.toAccount]?.regTypeShortName ?? "",
                    }
                }) ?? [];
                // Set distributionReason control value if it exists
                if (this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateCode?.toUpperCase() === stateCodes.Mississippi) {
                    this.isEarlyDistributionReason = this.selectedDistributionReasonList?.some(
                        dist => dist.distributionReason?.description?.toUpperCase() === distributionReasons.early
                    );
                    //Re-assign validator and update validity
                    this.taxWithholdingsFormGroup.setValidators(
                        CustomValidators.requiresStateTaxForEarlyDistributionForInterTransfers(this.isEarlyDistributionReason)
                    );
                    this.taxWithholdingsFormGroup.updateValueAndValidity();
                }
            }
        })

        this.actions$.pipe(ofType(TransactionFormActions.distributionReasonChangedMultipleIT)).subscribe(() => {
            setTimeout(() => {
                this.getAccountsSelectedWithZeroDist();
                if (
                (!this.accountsSelectedWithZeroDist || this.accountsSelectedWithZeroDist.length === 0) && this.isOneTimeTotalDist
                ) {
                    this.fedTaxPercent?.setValue(percentFormatter(this.taxWithholdings?.defaultFedTax ?? ''));
                    this.stateTaxPercent?.setValue(percentFormatter(this.taxWithholdings?.defaultStateTax ?? ''));
                }
            });
        });

        this.store.select(selectNraIndicator).pipe(takeUntil(this.destroy$)).subscribe((nraIndicator) => {
          this.nraIndicator = nraIndicator ?? false;
          if (nraIndicator) {
            this.resetAndLockTaxFields();
          }
        });

        this.store.select(selectClassifications).pipe(takeUntil(this.destroy$)).subscribe(accountClassifications => {
            this.accountClassifications = accountClassifications;
        });

        this.store.select(selectInternalTransfersInfo).pipe(takeUntil(this.destroy$)).subscribe(itInfo => {
            let verifyListResponse = itInfo.toAccountPropertiesById;
            const accounts = Object.keys(verifyListResponse);
            accounts.forEach(account => {
                if (verifyListResponse[account].verifyErrorText.toLowerCase() === fedTaxErrorMap.cashieringInternalTransferInternationalFedTaxError.toLowerCase()) {
                    this.fedTaxPercent?.setErrors({ 'internationalFedTaxError': true });
                    this.hasInternationalFedTaxError = true;
                }
            })
        })
    }

    GetFedTaxFormControl() {
        return new UntypedFormControl(this.fedTaxFormatted, [
            Validators.required,
            CustomValidators.zeroOrGreater,
            CustomValidators.wholeNumber,
            CustomValidators.greaterThan99FedRequestCheck,
            Validators.maxLength(10)
        ]);
    }

    GetStateTaxFormControl() {
        if (this.specialStateTaxFFEnabled){
            return new UntypedFormControl(this.stateTaxFormatted, [
                Validators.required,
                CustomValidators.zeroOrGreater,
                CustomValidators.greaterThan99State,
                CustomValidators.isANumber,
                Validators.maxLength(10),
            ]);
        } else {
            return new UntypedFormControl(this.stateTaxFormatted, [
                Validators.required,
                CustomValidators.zeroOrGreater,
                CustomValidators.greaterThan99State,
                CustomValidators.isANumber,
                Validators.maxLength(10),
                CustomValidators.belowMIStateTax(this.stateTaxPercent, this.stateCode, this.taxWithholdings.defaultStateTax ?? '0'),
                CustomValidators.belowMNStateTax(this.stateTaxPercent, this.stateCode, this.taxWithholdings.defaultStateTax ?? '0'),
            ]);
        }
    }

    get fedTaxFormatted() {
        return percentFormatter(this.taxWithholdings?.fedTaxPercent);
    }

    get stateTaxFormatted() {
        return percentFormatter(this.taxWithholdings?.stateTaxPercent);
    }

    get taxWithholdingsFormGroup() {
        return this.parentForm.controls
            .taxWithholdingsFormGroup as UntypedFormGroup;
    }

    get fedTaxPercent() {
        return this.taxWithholdingsFormGroup?.controls.fedTaxPercent;
    }

    get stateTaxPercent() {
        return this.taxWithholdingsFormGroup?.controls.stateTaxPercent;
    }

    get amountType() {
        return this.parentForm.controls?.amountType;
    }

    get isFedTaxInvalid(): boolean {
        return (
            (this.fedTaxPercent?.touched && this.fedTaxPercent?.invalid) ||
            this.fedAndStateTaxSumError !== ''
        );
    }

    get isStateTaxInvalid(): boolean {
        return (
            (this.stateTaxPercent?.touched && this.stateTaxPercent?.invalid) ||
            this.fedAndStateTaxSumError !== ''
        );
    }

    get fedTaxError() {
        if (this.fedTaxPercent && this.fedTaxPercent.errors) {
            const errorKeys: string[] = Object.keys(this.fedTaxPercent.errors ?? {});
            if (errorKeys.length > 0) {
                return fedTaxErrorMap[errorKeys[0] as keyof typeof fedTaxErrorMap];
            }
        }
    }

    get stateTaxError() {
        if (this.stateTaxPercent && this.stateTaxPercent.errors || this.hasMississippiStateTaxErrorText) {
            var stateTaxErrorMS = this.featureFlagService.featureFlags.enableSpecialStateTax && this.taxWithholdingsFormGroup?.errors?.stateTaxErrorTextMS ? { 'stateTaxErrorTextMS': true } : null;
            const errorKeys: string[] = Object.keys(
                (stateTaxErrorMS ? stateTaxErrorMS : this.stateTaxPercent.errors) ?? {},
            );
            if (errorKeys.length > 0) {
                return stateTaxErrorMap[errorKeys[0] as keyof typeof stateTaxErrorMap];
            }
        }
    }

    get fedAndStateTaxSumError(): string {
        if (this.taxWithholdingsFormGroup && this.taxWithholdingsFormGroup.errors) {
            const errorKeys: string[] = Object.keys(
                this.taxWithholdingsFormGroup.errors ?? {}
            );
            this.hasOnlyStateTaxError = this.featureFlagService.featureFlags.enableSpecialStateTax ? errorKeys.length === 1 && errorKeys.includes('stateTaxErrorTextMS') : false;
            if (errorKeys.length > 0 && !this.hasOnlyStateTaxError) {
                return 'Total tax withholding cannot exceed 99.99%. Update values or request a one-time check to withhold 100%';
            }
        }
        return '';
    }

    get showAndSetConvDistributionWarning() { 
        return this.selectedDistributionReasonList?.some(selectedDistributionReson => {
            if(selectedDistributionReson.distributionReason.subCode === RothConversion.DistributionReasons.CONVERSION_SUB_CODE) {
                if(selectedDistributionReson.distributionReason.code.trim() === RothConversion.DistributionReasons.UNDER_59_AND_A_HALF_CONVERSION_CODE && 
                    !this.accountClassifications?.isCustomer59OrOlder) {
                        this.convWarningText = RothWarnings.under59InputWarning;
                        
                    }
                if(selectedDistributionReson.distributionReason.code.trim() === RothConversion.DistributionReasons.OVER_59_AND_A_HALF_CONVERSION_CODE && 
                    this.accountClassifications?.isCustomer59OrOlder) {
                        this.convWarningText = RothWarnings.over59InputWarning;
                    }
                return true;
            }
            this.convWarningText = "";
            return false;
        }); 
    }

    get showAndSetZeroPercentDistributionWarning() {
        this.getAccountsSelectedWithZeroDist();
        if (this.accountsSelectedWithZeroDist && this.accountsSelectedWithZeroDist.length > 0) {
            if (this.isOneTimeTotalDist){
                this.resetAndLockTaxFields();
            }
            this.zeroPercentWarningText = RothWarnings.QualifiedComb0InputDistributionWarning + '\n\n'
            this.zeroPercentTitleWarningText = RothWarnings.ZeroPercentWarning;
            this.accountsSelectedWithZeroDist.forEach((toAccount) => {
                this.zeroPercentWarningText = this.zeroPercentWarningText + toAccount.accountRegistration + ' (' + toAccount.toAccount + ' | ' + toAccount.accountRegTypeCode + '); ';
            });
            this.zeroPercentWarningText = this.zeroPercentWarningText.slice(0, -2);
            return true;
        }
        else {
            if(this.isOneTimeTotalDist){
                this.fedTaxPercent?.enable();
                this.stateTaxPercent?.enable();
            }
            this.zeroPercentWarningText = '';
            this.zeroPercentTitleWarningText = '';
            return false;
        }
    }

    onFedTaxBlur(): void {
        if (this.fedTaxPercent.valid) {
            this.fedTaxPercent?.setValue(percentFormatter(this.fedTaxPercent.value));
        }
    }

    onStateTaxBlur(): void {
        if (this.stateTaxPercent?.valid) {
            this.stateTaxPercent?.setValue(
                percentFormatter(this.stateTaxPercent.value)
            );
        }
    }

    resetAndLockTaxFields(): void {
        this.fedTaxPercent?.setValue(Constants.zeroPercent);
        this.fedTaxPercent?.disable();
        this.stateTaxPercent?.setValue(Constants.zeroPercent);
        this.stateTaxPercent?.disable();
        this.disableAmountType();
    }

    disableAmountType(retainValue: boolean = false): void {
        if (!retainValue) {
          this.amountType?.setValue(amountTypeDict.gross);
        }
        this.amountType?.disable();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    getAccountsSelectedWithZeroDist() {
        this.accountsSelectedWithZeroDist = this.selectedDistributionReasonList?.filter(distributionReasonSelected =>
            (((distributionReasonSelected.distributionReason?.subCode === RothConversion.DistributionReasons.COMBINATION_SUB_CODE &&
                distributionReasonSelected.distributionReason?.code?.trim() === RothConversion.DistributionReasons.COMBINATION_CONVERSION_CODE) ||
                distributionReasonSelected.distributionReason?.code?.trim() === RothConversion.DistributionReasons.QUALIFIED_CODE)
            ));
    }

    get showMassachusettsDistributionWarning(): boolean {
        return this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateCode?.toUpperCase() === stateCodes.Massachusetts;
    }

    get showMississippiEarlyDistributionWarning(): boolean {
        return this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateCode?.toUpperCase() === stateCodes.Mississippi && this.isEarlyDistributionReason;
    }

    get stateTaxAttentionRequiredWarningMessage() {
        if (this.stateCode?.toUpperCase() === stateCodes.Massachusetts) {
            return Constants.retirementStateTaxAttentionRequiredTextMA;
        } else if (this.stateCode?.toUpperCase() === stateCodes.Mississippi) {
            return Constants.retirementStateTaxAttentionRequiredTextMS;
        }
        else {
            return '';
        }
    }

    get hasMississippiStateTaxErrorText(): boolean {
        return this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateCode?.toUpperCase() === stateCodes.Mississippi && this.taxWithholdingsFormGroup?.errors?.stateTaxErrorTextMS;
    }
}
